import React from "react";
import { Navigate, useParams } from "react-router-dom";

const Home = React.lazy(() => import("../pages/home"));
const Forgot = React.lazy(() => import("../pages/forgot"));
const ResetPasswordPage = React.lazy(() => import("../pages/reset-password"));
const ActivatePage = React.lazy(() => import("../pages/activate"));
const Profile = React.lazy(() => import("../pages/profile"));
const ProfileEditPage = React.lazy(() => import("../pages/profile/edit"));
const FriendsPage = React.lazy(() => import("../pages/friends"));
const PostPage = React.lazy(() => import("../pages/post/PostPage"));
const Messages = React.lazy(() => import("../pages/messages"));
const MessageByUsername = React.lazy(() => import("../pages/messages/MessageByUsername"));
const Landing = React.lazy(() => import("../pages/landing"));
const GroupsAll = React.lazy(() => import("../pages/groups/all"));
const GroupView = React.lazy(() => import("../pages/groups/view"));
const EventsAll = React.lazy(() => import("../pages/events/all"));
const EventView = React.lazy(() => import("../pages/events/view"));
const EventCalendar = React.lazy(() => import("../pages/events/calendar"));
const AddEvent = React.lazy(() => import("../pages/events/add"));
const Conference = React.lazy(() => import("../pages/conference"));
const CallPage = React.lazy(() => import("../pages/call"));
const FilesAll = React.lazy(() => import("../pages/files/all"));
const AddFile = React.lazy(() => import("../pages/files/add"));
const VideosAll = React.lazy(() => import("../pages/videos/all"));
const AddVideo = React.lazy(() => import("../pages/videos/add"));
const PollsAll = React.lazy(() => import("../pages/polls/all"));
const Mp3All = React.lazy(() => import("../pages/mp3/all"));
const AddMp3 = React.lazy(() => import("../pages/mp3/add"));
const GroupMessagesList = React.lazy(() => import("../pages/groupMessages/list"));
const GroupMessagesCreate = React.lazy(() => import("../pages/groupMessages/create"));
const UserCategoriesPage = React.lazy(() => import("../pages/userCategories"));
const SiteMembersPage = React.lazy(() => import("../pages/siteMembers"));
const InvitePage = React.lazy(() => import("../pages/invite"));
const SitePage = React.lazy(() => import("../pages/site"));
const Notifications = React.lazy(() => import("../pages/notifications"));
const Search = React.lazy(() => import("../pages/search"));
const Members = React.lazy(() => import("../pages/members"));
const ForumIndexPage = React.lazy(() => import("../pages/forum"));
const ForumTopicPage = React.lazy(() => import("../pages/forum/topic"));

function RedirectToPost() {
  const { post } = useParams();
  return <Navigate to={`/post/${encodeURIComponent(post || "")}`} replace />;
}

function RedirectToGroup() {
  const { guid, tab } = useParams();
  const groupPath = `/group/${encodeURIComponent(guid || "")}`;
  const tabPath = tab ? `/${encodeURIComponent(tab)}` : "";
  return <Navigate to={`${groupPath}${tabPath}`} replace />;
}

function RedirectToMessageConversation() {
  const { chatId } = useParams();
  return <Navigate to={`/messages/${encodeURIComponent(chatId || "")}`} replace />;
}

const pagesData = [
  {
    path: "",
    element: <Landing />,
    title: "landing",
    priv: false,
  },
  {
    path: "login",
    element: <Landing />,
    title: "login",
    priv: false,
  },
  {
    path: "resetlogin",
    element: <Forgot />,
    title: "resetlogin",
    priv: false,
  },
  {
    path: "forgot",
    element: <Forgot />,
    title: "forgot",
    priv: false,
  },
  {
    path: "reset-password/:token",
    element: <ResetPasswordPage />,
    title: "reset-password",
    priv: false,
  },
  {
    path: "site/:slug",
    element: <SitePage />,
    title: "site-page",
    priv: "optional",
  },
  {
    path: "home",
    element: <Home />,
    title: "home",
    priv: true,
  },
  {
    path: "search",
    element: <Search />,
    title: "search",
    priv: true,
  },
  {
    // Email activation link target (the server emails `${FRONTEND_URL}/activate/<token>`).
    // Optional auth so the link works for signed-out and signed-in visitors alike.
    path: "activate/:token",
    element: <ActivatePage />,
    title: "activate",
    priv: "optional",
  },
  {
    path: "profile",
    element: <Profile />,
    title: "profile",
    priv: true,
  },
  {
    path: "u/:username",
    element: <Profile />,
    title: "profile",
    priv: true,
  },
  {
    path: "u/:username/edit",
    element: <ProfileEditPage />,
    title: "profile-edit",
    priv: true,
  },
  {
    path: "u/:username/:subpage",
    element: <Profile />,
    title: "profile-subpage",
    priv: true,
  },
  {
    path: "messages",
    element: <Messages />,
    title: "messages",
    priv: true,
  },
  {
    path: "messages/all",
    element: <Messages />,
    title: "messages-all",
    priv: true,
  },
  {
    path: "messages/conversations/:chatId",
    element: <RedirectToMessageConversation />,
    title: "messages-conversation-compat",
    priv: true,
  },
  {
    path: "messages/:chatId",
    element: <Messages />,
    title: "messages",
    priv: true,
  },
  {
    path: "messages/message/:username",
    element: <MessageByUsername />,
    title: "messages-by-username",
    priv: true,
  },
  {
    path: "friends",
    element: <FriendsPage />,
    title: "profile",
    priv: true,
  },
  {
    path: "friends/:type",
    element: <FriendsPage />,
    title: "profile",
    priv: true,
  },
  {
    path: "profile/:username",
    element: <Profile />,
    title: "profile",
    priv: true,
  },
  {
    path: ":username/posts/:post",
    element: <PostPage />,
    title: "profile",
    priv: true,
  },
  {
    path: "posts/:post",
    element: <RedirectToPost />,
    title: "post-compat",
    priv: true,
  },
  {
    path: "post/:post",
    element: <PostPage />,
    title: "post",
    priv: true,
  },
  {
    path: "notifications",
    element: <Notifications />,
    title: "notifications",
    priv: true,
  },
  {
    path: "notifications/all",
    element: <Notifications />,
    title: "notifications-all",
    priv: true,
  },
  {
    path: "groups",
    element: <GroupsAll />,
    title: "groups-redirect",
    priv: true,
  },
  {
    path: "groups/all",
    element: <GroupsAll />,
    title: "groups",
    priv: true,
  },
  {
    path: "groups/my",
    element: <GroupsAll />,
    title: "groups-my",
    priv: true,
  },
  {
    path: "groups/owner/:ownerId",
    element: <GroupsAll />,
    title: "groups-owner",
    priv: true,
  },
  {
    path: "groups/:guid/:tab",
    element: <RedirectToGroup />,
    title: "group-tab-compat",
    priv: true,
  },
  {
    path: "groups/:guid",
    element: <RedirectToGroup />,
    title: "group-compat",
    priv: true,
  },
  {
    path: "group/:guid",
    element: <GroupView />,
    title: "group",
    priv: true,
  },
  {
    path: "group/:guid/:tab",
    element: <GroupView />,
    title: "group-tab",
    priv: true,
  },
  {
    path: "event/all",
    element: <EventsAll />,
    title: "events",
    priv: true,
  },
  {
    path: "event/list",
    element: <EventsAll />,
    title: "events-list",
    priv: true,
  },
  {
    path: "event/view/:guid",
    element: <EventView />,
    title: "event",
    priv: true,
  },
  {
    path: "event/view/:guid/:slug",
    element: <EventView />,
    title: "event-slug",
    priv: true,
  },
  {
    path: "event/calendar",
    element: <EventCalendar />,
    title: "event-calendar",
    priv: true,
  },
  {
    path: "event/my",
    element: <EventsAll />,
    title: "events-my",
    priv: true,
  },
  {
    path: "event/list/:userId",
    element: <EventsAll />,
    title: "events-user",
    priv: true,
  },
  {
    path: "event/add",
    element: <AddEvent />,
    title: "event-add",
    priv: true,
  },
  // Conference / Video Meeting
  {
    path: "conference/:eventId",
    element: <Conference />,
    title: "conference",
    priv: true,
  },
  // 1-on-1 Voice / Video Call
  {
    path: "call/:callId",
    element: <CallPage />,
    title: "call",
    priv: true,
  },
  {
    path: "members",
    element: <Members />,
    title: "members",
    priv: true,
  },
  // Files
  {
    path: "files/all",
    element: <FilesAll />,
    title: "files",
    priv: true,
  },
  {
    path: "files/my",
    element: <FilesAll />,
    title: "files-my",
    priv: true,
  },
  {
    path: "files/all/:userId",
    element: <FilesAll />,
    title: "files-user",
    priv: true,
  },
  {
    path: "files/add",
    element: <AddFile />,
    title: "files-add",
    priv: true,
  },
  // Videos
  {
    path: "video/list/all",
    element: <VideosAll />,
    title: "videos",
    priv: true,
  },
  {
    path: "videos/my",
    element: <VideosAll />,
    title: "videos-my",
    priv: true,
  },
  {
    path: "video/list/user/:userId",
    element: <VideosAll />,
    title: "videos-user",
    priv: true,
  },
  {
    path: "video/add",
    element: <AddVideo />,
    title: "video-add",
    priv: true,
  },
  // Polls
  {
    path: "polls/list/all",
    element: <PollsAll />,
    title: "polls",
    priv: true,
  },
  {
    path: "polls/my",
    element: <PollsAll />,
    title: "polls-my",
    priv: true,
  },
  {
    path: "polls/list/:userId",
    element: <PollsAll />,
    title: "polls-user",
    priv: true,
  },
  // MP3 / Audios
  {
    path: "mp3/all",
    element: <Mp3All />,
    title: "mp3",
    priv: true,
  },
  {
    path: "mp3/my",
    element: <Mp3All />,
    title: "mp3-my",
    priv: true,
  },
  {
    path: "mp3/all/:userId",
    element: <Mp3All />,
    title: "mp3-user",
    priv: true,
  },
  {
    path: "mp3/add",
    element: <AddMp3 />,
    title: "mp3-add",
    priv: true,
  },
  // Group Chat / Rooms
  {
    path: "group_messages/list",
    element: <GroupMessagesList />,
    title: "group-messages",
    priv: true,
  },
  {
    path: "group_messages/member",
    element: <GroupMessagesList />,
    title: "group-messages-member",
    priv: true,
  },
  {
    path: "group_messages/create",
    element: <GroupMessagesCreate />,
    title: "group-messages-create",
    priv: true,
  },
  // User Categories
  {
    path: "users/:catId/:catName",
    element: <UserCategoriesPage />,
    title: "user-categories",
    priv: true,
  },
  // Site Members by Gender
  {
    path: "site_members/:gender",
    element: <SiteMembersPage />,
    title: "site-members-gender",
    priv: true,
  },
  // Invite Friends
  {
    path: "invite",
    element: <InvitePage />,
    title: "invite",
    priv: true,
  },
  {
    path: "forum",
    element: <ForumIndexPage />,
    title: "forum",
    priv: true,
  },
  {
    path: "forum/topics/:id",
    element: <ForumTopicPage />,
    title: "forum-topic",
    priv: true,
  },
  // NOTE: The /administrator routes are now handled by the new AdminLayout
  // in client/src/pages/admin/ with nested React Router routes.
  // See router.jsx for the admin route tree.
];

export default pagesData;
